export interface AddressFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'textarea';
  required: boolean;
  validators?: any[];
  options?: { value: string; label: string }[];
  order: number;
}

export interface PMSAddressConfig {
  pmsType: string;
  fields: AddressFieldConfig[];
  validationMessages: { [key: string]: { type: string; message: string }[] };
}

import { PMS } from '../../enums/pms-name.enum';

export const PMS_ADDRESS_CONFIGS: { [key: string]: PMSAddressConfig } = {
  default: {
    pmsType: 'default',
    fields: [
      {
        key: 'address1',
        label: 'Address Line 1',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address2',
        label: 'Address Line 2',
        type: 'text',
        required: false,
        order: 2,
      },
      { key: 'town', label: 'Town', type: 'text', required: false, order: 3 },
      {
        key: 'county',
        label: 'County/State',
        type: 'text',
        required: false,
        order: 4,
      },
      {
        key: 'postcode',
        label: 'Postcode',
        type: 'text',
        required: false,
        order: 5,
      },
    ],
    validationMessages: {
      address1: [
        {
          type: 'required',
          message: 'You must enter the first line of your address',
        },
        {
          type: 'pattern',
          message:
            'You cannot include special characters, only numbers, letters and spaces',
        },
      ],
      address2: [
        {
          type: 'pattern',
          message:
            'You cannot include special characters, only numbers, letters and spaces',
        },
        { type: 'required', message: 'Address Line 2 is required' },
      ],
      town: [{ type: 'required', message: 'You must enter a town' }],
      county: [{ type: 'required', message: 'You must enter a county' }],
      postcode: [
        { type: 'required', message: 'You must enter a postcode' },
        {
          type: 'pattern',
          message: "Oops, looks like we didn't recognize that as a postcode",
        },
        {
          type: 'maxlength',
          message: 'Postcode must be less than 8 characters',
        },
        {
          type: 'minlength',
          message: 'Postcode must be at least 4 characters',
        },
      ],
    },
  },
  [PMS.RxWorks]: {
    pmsType: PMS.RxWorks,
    fields: [
      {
        key: 'address1',
        label: 'Address Line 1',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address2',
        label: 'Address Line 2',
        type: 'text',
        required: false,
        order: 2,
      },
      { key: 'town', label: 'Town', type: 'text', required: false, order: 3 },
      { key: 'state', label: 'State', type: 'text', required: false, order: 4 },
      {
        key: 'postcode',
        label: 'Zipcode',
        type: 'text',
        required: false,
        order: 5,
      },
    ],
    validationMessages: {
      address1: [
        { type: 'required', message: 'Address Line 1 is required' },
        { type: 'pattern', message: 'Invalid characters in address' },
      ],
      address2: [
        { type: 'pattern', message: 'Invalid characters in address' },
        { type: 'required', message: 'Address Line 2 is required' },
      ],
      town: [
        { type: 'required', message: 'Town is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      state: [
        { type: 'required', message: 'State is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      postcode: [
        { type: 'required', message: 'Zipcode is required' },
        { type: 'pattern', message: 'Invalid zipcode format' },
      ],
    },
  },
  [PMS.Robovet]: {
    pmsType: PMS.Robovet,
    fields: [
      {
        key: 'address1',
        label: 'Address',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address3',
        label: 'Town',
        type: 'text',
        required: false,
        order: 2,
      },
      {
        key: 'postcode',
        label: 'Postcode',
        type: 'text',
        required: false,
        order: 3,
      },
    ],
    validationMessages: {
      address1: [
        { type: 'required', message: 'Address is required' },
        { type: 'pattern', message: 'Invalid characters in address' },
      ],
      address3: [
        { type: 'required', message: 'Town is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      postcode: [
        { type: 'required', message: 'Postcode is required' },
        { type: 'pattern', message: 'Invalid postcode format' },
      ],
    },
  },
  [PMS.Merlin]: {
    pmsType: PMS.Merlin,
    fields: [
      {
        key: 'nameNo',
        label: 'Name/No',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address1',
        label: 'Address Line 1',
        type: 'text',
        required: false,
        order: 2,
      },
      {
        key: 'address2',
        label: 'Address Line 2',
        type: 'text',
        required: false,
        order: 3,
      },
      {
        key: 'address3',
        label: 'Town',
        type: 'text',
        required: false,
        order: 4,
      },
      { key: 'state', label: 'State', type: 'text', required: false, order: 5 },
      {
        key: 'postcode',
        label: 'Postcode',
        type: 'text',
        required: false,
        order: 6,
      },
    ],
    validationMessages: {
      nameNo: [
        { type: 'required', message: 'Name/No is required' },
        { type: 'pattern', message: 'Only numbers and letters allowed' },
      ],
      address1: [
        { type: 'required', message: 'Address Line 1 is required' },
        { type: 'pattern', message: 'Invalid characters in address' },
      ],
      address2: [
        { type: 'pattern', message: 'Invalid characters in address' },
        { type: 'required', message: 'Address Line 2 is required' },
      ],
      address3: [
        { type: 'required', message: 'Town is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      state: [
        { type: 'required', message: 'State is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      postcode: [
        { type: 'required', message: 'Postcode is required' },
        { type: 'pattern', message: 'Invalid UK postcode format' },
      ],
    },
  },
  [PMS.Teleos]: {
    pmsType: PMS.Teleos,
    fields: [
      {
        key: 'hNameNo',
        label: 'H name / No',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address1',
        label: 'Street',
        type: 'text',
        required: false,
        order: 2,
      },
      {
        key: 'address2',
        label: 'Locality',
        type: 'text',
        required: false,
        order: 3,
      },
      {
        key: 'address3',
        label: 'Town',
        type: 'text',
        required: false,
        order: 4,
      },
      {
        key: 'address4',
        label: 'County',
        type: 'text',
        required: false,
        order: 5,
      },
      {
        key: 'postcode',
        label: 'Postcode',
        type: 'text',
        required: false,
        order: 6,
      },
    ],
    validationMessages: {
      hNameNo: [
        { type: 'required', message: 'H name / No is required' },
        { type: 'pattern', message: 'Only numbers and letters allowed' },
      ],
      address1: [
        { type: 'required', message: 'Street is required' },
        { type: 'pattern', message: 'Invalid characters in street' },
      ],
      address2: [
        { type: 'pattern', message: 'Only letters and spaces allowed' },
        { type: 'required', message: 'Locality is required' },
      ],
      address3: [
        { type: 'required', message: 'Town is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      address4: [{ type: 'required', message: 'County is required' }],
      postcode: [
        { type: 'required', message: 'Postcode is required' },
        { type: 'pattern', message: 'Invalid postcode format' },
      ],
    },
  },
  [PMS.Provet]: {
    pmsType: PMS.Provet,
    fields: [
      {
        key: 'address1',
        label: 'Street address',
        type: 'text',
        required: false,
        order: 1,
      },
      {
        key: 'address2',
        label: 'Second line',
        type: 'text',
        required: false,
        order: 2,
      },
      {
        key: 'city',
        label: 'City/Town',
        type: 'text',
        required: false,
        order: 4,
      },
      {
        key: 'address3',
        label: 'State',
        type: 'text',
        required: false,
        order: 5,
      },
      {
        key: 'postcode',
        label: 'Post code',
        type: 'text',
        required: false,
        order: 6,
      },
    ],
    validationMessages: {
      address1: [
        { type: 'required', message: 'Street address is required' },
        { type: 'pattern', message: 'Invalid characters in address' },
      ],
      address2: [
        { type: 'pattern', message: 'Invalid characters in address' },
        { type: 'required', message: 'Address Line 2 is required' },
      ],
      address3: [
        { type: 'pattern', message: 'Invalid characters in address' },
        { type: 'required', message: 'State is required' },
      ],
      city: [
        { type: 'required', message: 'City is required' },
        { type: 'pattern', message: 'Only letters and spaces allowed' },
      ],
      postcode: [
        { type: 'required', message: 'Post code is required' },
        { type: 'pattern', message: 'Invalid post code format' },
      ],
    },
  },
};
